/**
 * Change Tracker - Simplified Entity-Level Tracking
 *
 * Tracks changes at entity level (pre-orders and flows) for offline sync.
 * No field-level tracking, no consolidation logic - just simple queue management.
 *
 * Simplified from 650 lines to ~200 lines.
 */

import type { SyncChange } from "../schema";

export interface ChangeTrackerOptions {
  syncQueue: SyncQueueStore;
}

export interface SyncQueueStore {
  add: (
    change:
      | SyncChange
      | Omit<SyncChange, "id" | "createdAt" | "retryCount" | "status">,
  ) => Promise<string>;
  getAll: () => Promise<SyncChange[]>;
  update: (id: string, updates: Partial<SyncChange>) => Promise<void>;
  remove: (ids: string[]) => Promise<void>;
  removeByEntityId: (entityId: string) => Promise<void>;
  getByEntityId: (entityId: string) => Promise<SyncChange[]>;
  getPending: () => Promise<SyncChange[]>;
  getFailed: () => Promise<SyncChange[]>;
  getSyncing: () => Promise<SyncChange[]>;
  getByPreOrderId: (preOrderId: string) => Promise<SyncChange[]>;
}

/**
 * Change Tracker - Manages sync queue for offline changes.
 */
export class ChangeTracker {
  private syncQueue: SyncQueueStore;

  constructor(syncQueue: SyncQueueStore) {
    this.syncQueue = syncQueue;
  }

  /**
   * Track a create operation.
   */
  async trackCreate(
    entityType: "pre-order" | "flow" | "partner",
    entityId: string,
    data: Record<string, any>,
  ): Promise<void> {
    const now = new Date().toISOString();

    const change: SyncChange = {
      id: crypto.randomUUID(),
      entityType,
      entityId,
      operation: "create",
      data,
      updatedAt: now,
      status: "pending",
      retryCount: 0,
      createdAt: Date.now(),
    };

    await this.syncQueue.add(change);
  }

  /**
   * Track an update operation.
   *
   * If there's already a pending change for this entity, we update it.
   * Otherwise, we create a new update change.
   *
   * @param entityType - Type of entity being updated
   * @param entityId - Entity ID
   * @param data - Update data (should include updated_at from the entity for conflict detection)
   */
  async trackUpdate(
    entityType: "pre-order" | "flow" | "partner",
    entityId: string,
    data: Record<string, any>,
  ): Promise<void> {
    // Use provided updated_at from entity, or fallback to current time
    // For proper conflict detection, the entity's updated_at should be passed in data
    const now = data.updated_at || new Date().toISOString();

    // Check for existing changes
    const existing = await this.syncQueue.getByEntityId(entityId);

    if (existing.length > 0) {
      // CRITICAL: Separate syncing changes from pending changes
      // We must NOT merge into syncing changes - they're being processed!
      const syncingChanges = existing.filter((c) => c.status === "syncing");
      const pendingChanges = existing.filter((c) => c.status === "pending");

      // If there are ANY syncing changes, create a new change (don't interfere)
      if (syncingChanges.length > 0) {
        await this.createUpdateChange(entityType, entityId, data, now);
        return;
      }

      // Only merge into the most recent PENDING change
      if (pendingChanges.length > 0) {
        const mostRecent = pendingChanges[pendingChanges.length - 1];

        if (mostRecent.operation === "create") {
          // Still in create phase, merge data
          await this.syncQueue.update(mostRecent.id, {
            data: { ...mostRecent.data, ...data },
            updatedAt: now,
          });
        } else if (mostRecent.operation === "update") {
          // Merge with existing update
          await this.syncQueue.update(mostRecent.id, {
            data: { ...mostRecent.data, ...data },
            updatedAt: now,
          });
        } else {
          // Create new update change
          await this.createUpdateChange(entityType, entityId, data, now);
        }
      } else {
        // No pending changes (only failed?), create new update
        await this.createUpdateChange(entityType, entityId, data, now);
      }
    } else {
      // No existing change, create new update
      await this.createUpdateChange(entityType, entityId, data, now);
    }
  }

  /**
   * Track a delete operation (DEPRECATED - use trackUpdate with deleted_at instead).
   * Kept for backwards compatibility, but soft delete via trackUpdate is preferred.
   */
  async trackDelete(
    entityType: "pre-order" | "flow" | "partner",
    entityId: string,
    data: Record<string, any> = {},
  ): Promise<void> {
    const now = new Date().toISOString();

    // Remove any existing changes for this entity
    await this.syncQueue.removeByEntityId(entityId);

    // Add delete change
    const change: SyncChange = {
      id: crypto.randomUUID(),
      entityType,
      entityId,
      operation: "delete",
      // For flow deletes, callers should pass { pre_order_id } so orphan
      // filtering and parent-id extraction in the sync executor can locate
      // the parent without an extra cache lookup.
      data,
      updatedAt: now,
      status: "pending",
      retryCount: 0,
      createdAt: Date.now(),
    };

    await this.syncQueue.add(change);
  }

  /**
   * Get all pending changes for sync.
   */
  async getPendingChanges(): Promise<SyncChange[]> {
    const all = await this.syncQueue.getAll();
    return all.filter((c) => c.status === "pending");
  }

  /**
   * Get pending changes for specific pre-orders.
   */
  async getChangesByPreOrderIds(preOrderIds: string[]): Promise<SyncChange[]> {
    const all = await this.getPendingChanges();

    // Filter to changes related to these pre-orders
    return all.filter((change) => {
      if (change.entityType === "pre-order") {
        return preOrderIds.includes(change.entityId);
      } else if (change.entityType === "flow") {
        // Check if flow belongs to one of these pre-orders
        return (
          change.data.pre_order_id &&
          preOrderIds.includes(change.data.pre_order_id)
        );
      }
      return false;
    });
  }

  /**
   * Mark changes as syncing.
   */
  async markAsSyncing(changeIds: string[]): Promise<void> {
    for (const id of changeIds) {
      await this.syncQueue.update(id, { status: "syncing" });
    }
  }

  /**
   * Mark changes as pending (for retry).
   */
  async markAsPending(changeIds: string[]): Promise<void> {
    for (const id of changeIds) {
      const changes = await this.syncQueue.getAll();
      const change = changes.find((c) => c.id === id);

      if (change) {
        await this.syncQueue.update(id, {
          status: "pending",
          retryCount: change.retryCount + 1,
          lastRetryAt: Date.now(),
        });
      }
    }
  }

  /**
   * Remove synced changes from queue.
   */
  async removeSyncedChanges(changeIds: string[]): Promise<void> {
    await this.syncQueue.remove(changeIds);
  }

  /**
   * Clear all pending changes (use with caution).
   */
  async clearAll(): Promise<void> {
    const all = await this.syncQueue.getAll();
    await this.syncQueue.remove(all.map((c) => c.id));
  }

  /**
   * Get count of pending changes.
   */
  async getPendingCount(): Promise<number> {
    const pending = await this.getPendingChanges();
    return pending.length;
  }

  /**
   * Get dirty pre-order IDs (pre-orders with pending, failed, or syncing changes).
   * Includes pre-orders that have flow changes.
   */
  async getDirtyPreOrderIds(): Promise<Set<string>> {
    const pending = await this.syncQueue.getPending();
    const failed = await this.syncQueue.getFailed();
    const syncing = await this.syncQueue.getSyncing();
    const all = [...pending, ...failed, ...syncing];

    const dirtyIds = new Set<string>();

    for (const change of all) {
      if (change.entityType === "pre-order") {
        // Pre-order change → add pre-order ID
        dirtyIds.add(change.entityId);
      } else if (change.entityType === "flow" && change.data.pre_order_id) {
        // Flow change → add the parent pre-order ID
        dirtyIds.add(change.data.pre_order_id);
      }
    }

    return dirtyIds;
  }

  /**
   * Get dirty flow IDs within a pre-order (flows with pending, failed, or syncing changes).
   */
  async getDirtyFlowIds(preOrderId: string): Promise<Set<string>> {
    const changes = await this.syncQueue.getByPreOrderId(preOrderId);
    const flowIds = new Set<string>();

    for (const change of changes) {
      if (change.entityType === "flow") {
        flowIds.add(change.entityId);
      }
    }

    return flowIds;
  }

  /**
   * Get partners that need to be created (pending partner create operations).
   */
  async getPartnersToCreate(): Promise<SyncChange[]> {
    const all = await this.syncQueue.getAll();
    return all.filter(
      (change) =>
        change.entityType === "partner" && change.operation === "create",
    );
  }

  /**
   * Update partner IDs in pending changes after partner sync.
   * Replaces temporary partner IDs with real server IDs.
   */
  async updatePartnerIdInPendingChanges(
    tempPartnerId: string,
    realPartnerId: string,
  ): Promise<void> {
    const all = await this.syncQueue.getAll();

    for (const change of all) {
      if (change.status !== "pending") continue;

      // Check if this change references the temp partner ID
      let updated = false;
      const newData = { ...change.data };

      if (newData.partner_id === tempPartnerId) {
        newData.partner_id = realPartnerId;
        updated = true;
      }

      if (updated) {
        await this.syncQueue.update(change.id, { data: newData });
      }
    }
  }

  // Private helper methods

  private async createUpdateChange(
    entityType: "pre-order" | "flow" | "partner",
    entityId: string,
    data: Record<string, any>,
    timestamp: string,
  ): Promise<void> {
    const change: SyncChange = {
      id: crypto.randomUUID(),
      entityType,
      entityId,
      operation: "update",
      data,
      updatedAt: timestamp,
      status: "pending",
      retryCount: 0,
      createdAt: Date.now(),
    };

    await this.syncQueue.add(change);
  }
}
